import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Heading } from "@/design-system/components/Heading";

/**
 * Phase 8a — single-flag admin gate (user.role === "admin"). V2 light-first
 * tokens (Batch C4 migration). This component is kept for backwards
 * compatibility with imports referencing `@/admin/AdminRoute`; the canonical
 * combined guard lives at `@/auth/ProtectedRoute` (see `AdminRoute` export
 * there).
 */
export function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div
        data-testid="admin-route-loading"
        className="min-h-screen flex items-center justify-center bg-v2-background text-v2-textMuted"
      >
        <div className="text-sm">Verifying admin access…</div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (user.role !== "admin") {
    return (
      <div
        data-testid="admin-route-forbidden"
        className="min-h-screen flex items-center justify-center bg-v2-background"
      >
        <div className="text-center max-w-md px-6">
          <Heading level={1} size="xl" className="mb-3">
            Admin access required
          </Heading>
          <p className="text-sm text-v2-textMuted mb-6">
            You're signed in as a member. The admin console is restricted to
            staff accounts.
          </p>
          <a
            href="/dashboard"
            data-testid="admin-route-back-to-app"
            className="inline-flex items-center gap-2 rounded-md border border-v2-border px-4 py-2 text-sm text-v2-text hover:bg-v2-surface transition-colors"
          >
            Back to dashboard
          </a>
        </div>
      </div>
    );
  }
  return children;
}

export default AdminRoute;
